import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';

import React, { useEffect, useState } from 'react';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import { MdEdit } from 'react-icons/md';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ProceduresModal } from '../components/ProceduresModal';
import DeleteModal from 'components/deleteModal/DeleteModal';
import { createAPIEndPoint } from 'config/api/api';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Loader from 'loaders/Loader';
import moment from 'moment';
import { logoutUser } from 'utils';
import { toProperCase } from 'utils/formatting';

const columnHelper = createColumnHelper();

// Data for Options and Procedure Steps

export default function ProceduresDetails(props) {
  const navigate = useNavigate();
  const location = useLocation();
  // const { id } = location.state;
  const pathParts = location.pathname.split('/');
  const id = pathParts[pathParts.length - 1];
  const { ...rest } = props;
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [loading, setLoading] = useState(true);
  const [procedure, setProcedure] = useState({});
  console.log('ProceduresDetails ~ procedure:', procedure);

  const getProcedure = async () => {
    try {
      setLoading(true);
      const response = await createAPIEndPoint('procedure').fetchById(`/${id}`);
      const procedure = response.data.procedure;

      setProcedure(procedure);

      setLoading(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Error fetching team details',
      );
      if (error?.response?.data?.error.includes('Bearer token')) {
        logoutUser(navigate);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getProcedure();
  }, [id]);

  // Options Table Columns
  const optionsColumns = [
    columnHelper.accessor('option_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Option Name
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('additional_cost', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Additional Cost
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue()}
        </Text>
      ),
    }),
  ];

  // Procedure Steps Table Columns
  const procedureStepsColumns = [
    columnHelper.accessor('step_number', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Step Number
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('step_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Step Name
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('step_description', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Description
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('step_price', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Price
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Date Created
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()
            ? moment(info.getValue()).format('MM/DD/YYYY')
            : 'N/A'}
        </Text>
      ),
    }),
  ];

  const [optionsData, setOptionsData] = useState([]);
  console.log(' ProceduresDetails ~ optionsData:', optionsData);
  const [procedureStepsData, setProcedureStepsData] = useState([]);

  useEffect(() => {
    setOptionsData(procedure.options || []);
    setProcedureStepsData(procedure.steps || []);
  }, [procedure]);

  // React Table instance for Options
  const optionsTable = useReactTable({
    data: optionsData,
    columns: optionsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // React Table instance for Procedure Steps
  const procedureStepsTable = useReactTable({
    data: procedureStepsData,
    columns: procedureStepsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [type, setType] = React.useState('add');

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleOpenDeleteModal = () => setDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setDeleteModalOpen(false);

  // Chakra Color Mode
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'gray.400';
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const handleEditClick = (e) => {
    onOpen();
    setType('edit');
  };

  const handleDeleteItem = () => {
    // Perform your delete logic here, e.g., API call to delete the item
    console.log('Item deleted!');
  };

  return (
    <>
      {loading && <Loader />}
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <BackButton />
        {/* Main Fields */}
        <Grid
          templateColumns={{
            base: '1fr',
            lg: '1fr',
          }}
          templateRows={{
            base: 'repeat(2, 1fr)',
            lg: '1fr',
          }}
          gap={{ base: '20px', xl: '20px' }}
        >
          <Card mb={{ base: '0px', '2xl': '20px' }} {...rest}>
            <Text
              color={textColorPrimary}
              // textTransform="lowercase"
              fontWeight="bold"
              fontSize="2xl"
              mt="8px"
              mb="24px"
              // sx={{
              //   '&:first-letter': {
              //     textTransform: 'uppercase',
              //   },
              // }}
            >
              {toProperCase(procedure.procedure_name) || 'N/A'}
            </Text>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {/* Display Clinic Information */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Code:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {procedure.code || 'N/A'}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Cost:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  ${procedure.cost || 'N/A'}
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Created Date:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {procedure.created_at
                    ? moment(procedure.created_at).format('MM/DD/YYYY')
                    : 'N/A'}
                </Text>
              </Box>
            </SimpleGrid>

            <SimpleGrid my={5}>
              {optionsData.length > 0 && (
                <Box
                  border="1px solid #E3E7EA"
                  p={4}
                  mb={5}
                  borderRadius="16px"
                >
                  <Text fontWeight="semibold" color={textColorPrimary}>
                    Options:
                  </Text>
                  <Box overflowX="auto">
                    <Table
                      variant="striped"
                      color="gray.500"
                      mb="24px"
                      mt="12px"
                    >
                      <Thead>
                        {optionsTable.getHeaderGroups().map((headerGroup) => (
                          <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <Th
                                key={header.id}
                                colSpan={header.colSpan}
                                pe="10px"
                                borderColor={borderColor}
                              >
                                <Flex
                                  justifyContent="space-between"
                                  align="center"
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </Flex>
                              </Th>
                            ))}
                          </Tr>
                        ))}
                      </Thead>
                      <Tbody>
                        {optionsTable.getRowModel().rows.map((row) => (
                          <Tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <Td
                                key={cell.id}
                                fontSize="14px"
                                borderColor="transparent"
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}

              {procedureStepsData.length > 0 && (
                <Box border="1px solid #E3E7EA" p={4} borderRadius="16px">
                  <Text fontWeight="semibold" color={textColorPrimary}>
                    Procedure Steps:
                  </Text>
                  <Box overflowX="auto">
                    <Table
                      variant="striped"
                      color="gray.500"
                      mb="24px"
                      mt="12px"
                    >
                      <Thead>
                        {procedureStepsTable
                          .getHeaderGroups()
                          .map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <Th
                                  key={header.id}
                                  colSpan={header.colSpan}
                                  pe="10px"
                                  borderColor={borderColor}
                                >
                                  <Flex
                                    justifyContent="space-between"
                                    align="center"
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                                  </Flex>
                                </Th>
                              ))}
                            </Tr>
                          ))}
                      </Thead>
                      <Tbody>
                        {procedureStepsTable.getRowModel().rows.map((row) => (
                          <Tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <Td
                                key={cell.id}
                                fontSize="14px"
                                borderColor="transparent"
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </SimpleGrid>
            <Box display="flex" justifyContent="flex-end">
              <Button
                minW={{ base: 'auto', md: '140px' }}
                variant="brand"
                fontWeight="500"
                mr={4}
                onClick={handleEditClick}
              >
                Edit
              </Button>
              {/* <Button
                minW={{ base: 'auto', md: '140px' }}
                colorScheme="red"
                fontWeight="500"
                onClick={handleOpenDeleteModal}
              >
                Delete
              </Button> */}
            </Box>
          </Card>
        </Grid>
        <ProceduresModal
          isOpen={isOpen}
          onClose={onClose}
          type={type}
          procedureId={id}
        />

        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onDelete={handleDeleteItem}
          itemId="123" // Item name to be deleted
        />
      </Box>
    </>
  );
}
